// src/components/HostDetail/HostDetail.jsx
import React, { useMemo, useState, useEffect } from "react"
import axios from "axios";
import styled from "styled-components"
import SkeletonLoader from "../../../../components/SkeletonLoader"
import { FiArrowLeft, FiStar } from "react-icons/fi"
import UnifiedEvaluationsTable from "./UnifiedEvaluationsTable";
import { useAvatar } from "../../../../hooks/useAvatar"
import { buildQuizHeaders } from "../../../quiz/utils/buildQuizHeaders";
import { getUserQuizzes } from "../../../quiz/services/userStatsService";
import { useTenantTopicsAccess } from "../../../quiz/hooks/useTenantTopicsAccess";
import { useQuizTopics } from "../../../quiz/hooks/useQuizTopics";
import { useQuizTopicProgress } from "../../../quiz/hooks/useQuizTopicProgress";
import {
  QuizEmptyMessage as EmptyMessage,
  QuizErrorMessage as ErrorMessage,
  QuizProfileWrapper as Wrapper,
  QuizSectionTitle as SectionTitle,
  QuizSummaryCard,
  QuizSummaryGrid,
  QuizTabButton as TabButton,
  QuizTabs as Tabs,
  QuizTopicCard as TopicCard,
  QuizTopicHeader as TopicHeader,
} from "../../../quiz/components/QuizProfileLayout";
import endpoint from "../../../../Endpoint";
import { getHostRestoDetailsById } from "../../../../services/restoHostService";
import AxiosService from "../../../../lib/axios";

// ========== Styled Components ==========

// --- Botón volver ---
const TopBar = styled.div`
  margin-bottom: 0.5rem;
`
const BackButton = styled.button`
  background: none;
  border: none;
  color: #444;
  display: flex;
  align-items: center;
  gap: 0.3rem;
  cursor: pointer;
  font-size: 0.9rem;
`

// --- Header principal ---
const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`
const Left = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
`
const Avatar = styled.img`
  width: 52px;
  height: 52px;
  border-radius: 50%;
`
const Text = styled.div``
const Label = styled.span`
  font-size: 0.8rem;
  color: #999;
  display: block;
`
const Name = styled.h3`
  margin: 0;
  font-size: 1.2rem;
  font-weight: 500;
`
const CsvButton = styled.button`
  border: 1px solid #d1d5db;
  background: #fff;
  color: #111827;
  border-radius: 999px;
  padding: 6px 10px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`
const EvaluationActions = styled.div`
  display: flex;
  justify-content: flex-start;
  margin: 0 0 10px;
`
// --- Tabla ---
const TableWrapper = styled.div`
  overflow-x: auto;
`
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;

  th {
    text-align: left;
    padding: 0.6rem;
    font-weight: 600;
    color: #444;
    border-bottom: 1px solid #ddd;
  }

  td {
    padding: 1rem 0.6rem;
    color: #333;
  }

  tbody tr:nth-child(even) {
    background: #f9f9f9;
  }
`
const EvalRow = styled.tr`
  td {
    padding: 0.5rem 0.6rem;
  }
`
const EvalMetrics = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 5px;
  font-size: 0.8rem;
  color: #444;
`

const EvalMetric = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5px;
  align-items: center;
`

// --- Estrellas ---
const StarsWrapper = styled.span`
  display: grid;
  grid-template-columns: .5fr .5fr .01fr;
  gap: 5px;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #e63946;

  span {
    color: #eaeaea;
  }
`

const PaginationContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 16px 0 4px;
`;

const PaginationInfo = styled.div`
  font-size: 12px;
  color: #777;
  text-align: center;
`;

const PaginationControls = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const PaginationButton = styled.button`
  min-width: 32px;
  height: 32px;
  padding: 0 10px;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: ${({ disabled, active }) => (active ? "#111" : disabled ? "#f3f3f3" : "#fff")};
  color: ${({ disabled, active }) => (active ? "#fff" : disabled ? "#aaa" : "#333")};
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  font-size: 12px;
  font-weight: ${({ active }) => (active ? "700" : "500")};
`;

const QUIZ_HISTORY_PAGE_SIZE = 5;
const DETAIL_PAGE_SIZE = 20;

const getHostId = (host) => host?._id || host?.id || "";

const getTopicId = (topic) => topic?._id || topic?.id || topic?.topic_id || "";

const getTopicName = (topic) => topic?.name || topic?.title || "Topico";

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const toFixedOne = (value) => toNumber(value, 0).toFixed(1);

const normalizeBooleanScore = (value) => {
  if (typeof value === "boolean") {
    return value ? 5 : 0;
  }
  return value;
};

const sortByCreatedAtDesc = (items = []) =>
  [...items].sort((a, b) => new Date(b?.createdAt || 0) - new Date(a?.createdAt || 0));

const getPageSlice = (items = [], currentPage = 1, pageSize = DETAIL_PAGE_SIZE) => {
  const start = (currentPage - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

const getVisiblePages = (currentPage, totalPages) => {
  if (totalPages <= 5) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  if (currentPage <= 3) {
    return [1, 2, 3, 4, "...", totalPages];
  }

  if (currentPage >= totalPages - 2) {
    return [1, "...", totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
  }

  return [1, "...", currentPage - 1, currentPage, currentPage + 1, "...", totalPages];
};

function TablePagination({
  currentPage,
  totalItems,
  pageSize = DETAIL_PAGE_SIZE,
  onPageChange,
}) {
  const totalPages = Math.ceil(totalItems / pageSize);

  if (totalPages <= 1) return null;

  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <PaginationContainer>
      <PaginationInfo>
        Mostrando {startItem}-{endItem} de {totalItems}
      </PaginationInfo>
      <PaginationControls>
        <PaginationButton
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          ‹
        </PaginationButton>
        {getVisiblePages(currentPage, totalPages).map((page, index) =>
          page === "..." ? (
            <PaginationInfo key={`ellipsis-${index}`}>...</PaginationInfo>
          ) : (
            <PaginationButton
              key={page}
              type="button"
              active={page === currentPage}
              onClick={() => onPageChange(page)}
            >
              {page}
            </PaginationButton>
          )
        )}
        <PaginationButton
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          ›
        </PaginationButton>
      </PaginationControls>
    </PaginationContainer>
  );
}

const calculateRealProgress = ({ correctAnswers, totalQuestions }) => {
  const total = toNumber(totalQuestions, 0);
  if (total <= 0) return 0;
  const correct = Math.max(0, Math.min(toNumber(correctAnswers, 0), total));
  return (correct / total) * 100;
};

const formatQuizStatus = (status, progress) => {
  if (toNumber(progress, 0) >= 100) {
    return "Completado";
  }
  switch (String(status || "").toLowerCase()) {
    case "in_progress":
      return "En progreso";
    case "not_started":
      return "No iniciado";
    default:
      return status || "-";
  }
};

// ========== Subcomponentes ==========
function Stars({ value = 0 }) {
  return (
    <StarsWrapper>
      {value} <FiStar className="star filled" /> <span>|</span>
    </StarsWrapper>
  )
}

// ========== Componente principal ==========
export default function HostDetail({
  host,
  onBack,
  loading = false,
  error = null,
  canSeeEvaluations = false,
  canSeeQuizResults = false,
  restoId: providedRestoId = "",
}) {
  const { getAvatar } = useAvatar()
  const hostId = getHostId(host);
  const [activeTab, setActiveTab] = useState("puntos")
  const [evaluaciones, setEvaluaciones] = useState([])
  const [transactions, setTransactions] = useState([])
  const [transactionsPage, setTransactionsPage] = useState(1);
  const [evaluationsPage, setEvaluationsPage] = useState(1);
  const [loader, setLoader] = useState(true);
  const [detailsError, setDetailsError] = useState("");
  const [quizHistoryByTopic, setQuizHistoryByTopic] = useState({});
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizError, setQuizError] = useState("");

  const quizHeaders = useMemo(() => {
    if (typeof window === "undefined") {
      return buildQuizHeaders({ userId: hostId, userName: host?.name });
    }
    const tenantKey =
      localStorage.getItem("tenantApiKey") ||
      localStorage.getItem("tenant_api_key") ||
      undefined;
    return buildQuizHeaders({
      ...(tenantKey ? { tenantKey } : {}),
      userId: hostId,
      userName: host?.name,
    });
  }, [hostId, host?.name]);

  const {
    topicIds: allowedTopicIds,
    loading: quizAccessLoading,
    error: quizAccessError,
  } = useTenantTopicsAccess({
    enabled: Boolean(activeTab === "quizzes" && hostId),
  });

  const {
    topics: redeemedTopics,
    loading: quizTopicsLoading,
    error: quizTopicsError,
  } = useQuizTopics({
    headers: quizHeaders,
    allowedTopicIds,
    enabled: Boolean(
      activeTab === "quizzes" &&
      hostId &&
      !quizAccessLoading &&
      Array.isArray(allowedTopicIds)
    ),
  });

  const {
    progressById: quizProgressByTopic,
    loading: quizProgressLoading,
    error: quizProgressError,
  } = useQuizTopicProgress({
    topics: redeemedTopics,
    userId: hostId,
    headers: quizHeaders,
    enabled: Boolean(activeTab === "quizzes" && hostId && redeemedTopics.length > 0),
  });

  const restoId =
    providedRestoId ||
    host?.restoId ||
    host?.resto_id ||
    host?.restaurantId ||
    host?.restaurant_id ||
    host?.place_id ||
    "";
  const canDownloadEvaluationsCsv = Boolean(
    restoId
  );

  const handleDownloadCsv = async () => {
    if (!hostId || !canDownloadEvaluationsCsv) return;

    try {
      const token = AxiosService.getToken();
      const response = await axios({
        method: "GET",
        url: `${endpoint}/api/v2/evaluations/csv/host/${hostId}/${restoId}`,
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `evaluaciones-host-${hostId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (_) {
      setDetailsError("No se pudo descargar el CSV del host.");
    }
  }

  const splitDateTime = isoString => {
    const dateObj = new Date(isoString)
    const date = dateObj.toLocaleDateString("es-CL")
    const time = dateObj.toLocaleTimeString("es-CL", { hour12: false })
    return { date, time }
  }

  useEffect(() => {
    if (loading) {
      setLoader(true);
      setDetailsError("");
      setEvaluaciones([]);
      setTransactions([]);
      return;
    }

    if (!hostId) {
      setLoader(false);
      setDetailsError("");
      setEvaluaciones([]);
      setTransactions([]);
      return;
    }

    let isMounted = true;

    const loadHostDetails = async () => {
      try {
        setLoader(true);
        setDetailsError("");

        const details = await getHostRestoDetailsById(hostId);
        if (!isMounted) return;

        setEvaluaciones(
          sortByCreatedAtDesc(details?.evaluations || []).map((e, i) => {
            const { date = "-", time = "-" } = splitDateTime(e?.createdAt)
            const { ip = "-", country = "-", device = "-" } = e?.metadata || {}
            return {
              id: i + 1,
              date,
              time,
              device,
              location: country,
              ip,
              ratings: {
                limpieza: normalizeBooleanScore(e?.question1),
                atencion: normalizeBooleanScore(e?.question2),
                rapidez: normalizeBooleanScore(e?.question3),
                ayuda: normalizeBooleanScore(e?.question4),
                resolucion: normalizeBooleanScore(e?.question5),
              },
            }
          })
        );

        setTransactions(
          sortByCreatedAtDesc(details?.transactions || []).map((e, i) => {
            const { date = "-", time = "-" } = splitDateTime(e?.createdAt)
            const { ip = "-", country = "-", device = "-" } = e?.metadata || {}
            return {
              id: i + 1,
              date,
              time,
              device,
              location: country,
              ip,
            }
          })
        );
      } catch (err) {
        if (!isMounted) return;
        setEvaluaciones([]);
        setTransactions([]);
        setDetailsError(err?.message || "Error al cargar el detalle del host");
      } finally {
        if (isMounted) {
          setLoader(false);
        }
      }
    };

    loadHostDetails();

    return () => {
      isMounted = false;
    };
  }, [hostId, loading])

  useEffect(() => {
    if (activeTab !== "quizzes") return;
    if (!hostId) return;
    if (quizAccessLoading || quizTopicsLoading) return;

    if (redeemedTopics.length === 0) {
      setQuizHistoryByTopic({});
      setQuizLoading(false);
      setQuizError("");
      return;
    }

    let isMounted = true;
    setQuizLoading(true);
    setQuizError("");
    Promise.allSettled(
      redeemedTopics.map(async (topic) => {
        const topicId = getTopicId(topic);
        const history = await getUserQuizzes(
          hostId,
          { page: 1, limit: QUIZ_HISTORY_PAGE_SIZE, status: "completed", topicId },
          quizHeaders
        );
        return {
          topicId,
          quizzes: history?.quizzes || [],
          hasNext: Boolean(history?.pagination?.has_next),
          page: 1,
          error: "",
        };
      })
    )
      .then((results) => {
        if (!isMounted) return;
        const nextHistoryByTopic = {};
        let firstError = "";

        results.forEach((result, index) => {
          const topicId = getTopicId(redeemedTopics[index]);
          if (result.status === "fulfilled") {
            nextHistoryByTopic[topicId] = result.value;
            return;
          }

          const message = result?.reason?.message || "No se pudo cargar el historial de la trivia.";
          nextHistoryByTopic[topicId] = {
            topicId,
            quizzes: [],
            hasNext: false,
            page: 1,
            error: message,
          };
          if (!firstError) {
            firstError = message;
          }
        });

        setQuizHistoryByTopic(nextHistoryByTopic);
        setQuizError(firstError);
      })
      .finally(() => {
        if (!isMounted) return;
        setQuizLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [
    activeTab,
    hostId,
    quizAccessLoading,
    quizTopicsLoading,
    quizHeaders,
    redeemedTopics,
  ]);

  const quizErrors = [quizAccessError, quizTopicsError, quizProgressError, quizError].filter(Boolean);
  const shouldShowQuizSkeleton =
    quizAccessLoading ||
    quizTopicsLoading ||
    quizProgressLoading ||
    (quizLoading && Object.keys(quizHistoryByTopic).length === 0);
  const showQuizEmptyState =
    !shouldShowQuizSkeleton &&
    quizErrors.length === 0 &&
    redeemedTopics.length === 0;
  const paginatedTransactions = getPageSlice(transactions, transactionsPage, DETAIL_PAGE_SIZE);
  const paginatedEvaluaciones = getPageSlice(evaluaciones, evaluationsPage, DETAIL_PAGE_SIZE);

  useEffect(() => {
    setTransactionsPage(1);
  }, [transactions]);

  useEffect(() => {
    setEvaluationsPage(1);
  }, [evaluaciones]);

  const availableTabs = [
    ...(canSeeEvaluations ? [{ id: "evaluaciones", label: "Evaluaciones" }] : []),
    ...(canSeeQuizResults ? [{ id: "quizzes", label: "Resultados de trivias" }] : []),
  ];

  useEffect(() => {
    if (availableTabs.length === 0) {
      if (activeTab !== null) setActiveTab(null);
      return;
    }
    if (availableTabs.some((tab) => tab.id === activeTab)) return;
    setActiveTab(availableTabs[0]?.id);
  }, [activeTab, availableTabs]);

  if (!loading && !loader && !hostId) {
    return (
      <Wrapper>
        {onBack && (
          <TopBar>
            <BackButton onClick={() => onBack?.()}>
              <FiArrowLeft /> Volver
            </BackButton>
          </TopBar>
        )}
        {(error || detailsError) && (
          <ErrorMessage>
            ⚠️ {error || detailsError}
          </ErrorMessage>
        )}
        <EmptyMessage>No hay datos del host disponibles</EmptyMessage>
      </Wrapper>
    );
  }

  return (
    <Wrapper>
      {/* Botón volver */}
      {onBack && (
        <TopBar>
          <BackButton onClick={() => onBack?.()}>
            <FiArrowLeft /> Volver
          </BackButton>
        </TopBar>
      )}

      {(error || detailsError) && (
        <ErrorMessage>
          ⚠️ {error || detailsError}
        </ErrorMessage>
      )}

      {/* Tabs */}
      {availableTabs.length > 0 && (
        <Tabs $scrollable={activeTab === "puntos" || activeTab === "evaluaciones"}>
          {availableTabs.map((tab) => (
            <TabButton key={tab.id} $active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
              {tab.label}
            </TabButton>
          ))}
        </Tabs>
      )}

      {/* Contenido */}
      {activeTab === "puntos" && (
        <TableWrapper>
          <Table>
            <thead>
              <tr>
                <th>#</th>
                <th>Fecha</th>
                <th>Hora</th>
                <th>Móvil</th>
                <th>Localización</th>
                <th>IP</th>
              </tr>
            </thead>
            <tbody>
              {(loading || loader) ? (
                [...Array(5)].map((_, i) => (
                  <tr key={`skeleton-${i}`}>
                    <td><SkeletonLoader active width="30px" height="16px" /></td>
                    <td><SkeletonLoader active width="100px" height="16px" /></td>
                    <td><SkeletonLoader active width="80px" height="16px" /></td>
                    <td><SkeletonLoader active width="120px" height="16px" /></td>
                    <td><SkeletonLoader active width="100px" height="16px" /></td>
                    <td><SkeletonLoader active width="120px" height="16px" /></td>
                  </tr>
                ))
              ) : transactions.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", color: "#888", padding: "40px" }}>
                    No hay transacciones disponibles
                  </td>
                </tr>
              ) : (
                paginatedTransactions.map(p => (
                  <tr key={p.id}>
                    <td>{p.id}</td>
                    <td>{p.date}</td>
                    <td>{p.time}</td>
                    <td>{p.device}</td>
                    <td>{p.location}</td>
                    <td>{p.ip}</td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <TablePagination
            currentPage={transactionsPage}
            totalItems={transactions.length}
            pageSize={DETAIL_PAGE_SIZE}
            onPageChange={setTransactionsPage}
          />
        </TableWrapper>
      )}

      {activeTab === "evaluaciones" && (
        <>
          <SectionTitle>Detalle por evaluación</SectionTitle>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Fecha</th>
                  <th>Hora</th>
                  <th>Móvil</th>
                  <th>Localización</th>
                  <th>IP</th>
                </tr>
              </thead>
              <tbody>
                {(loading || loader) ? (
                  [...Array(5)].map((_, i) => (
                    <React.Fragment key={`skeleton-eval-${i}`}>
                      <tr>
                        <td><SkeletonLoader active width="30px" height="16px" /></td>
                        <td><SkeletonLoader active width="100px" height="16px" /></td>
                        <td><SkeletonLoader active width="80px" height="16px" /></td>
                        <td><SkeletonLoader active width="120px" height="16px" /></td>
                        <td><SkeletonLoader active width="100px" height="16px" /></td>
                        <td><SkeletonLoader active width="120px" height="16px" /></td>
                      </tr>
                      <EvalRow>
                        <td colSpan={6}>
                          <SkeletonLoader active width="100%" height="40px" />
                        </td>
                      </EvalRow>
                    </React.Fragment>
                  ))
                ) : evaluaciones.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", color: "#888", padding: "40px" }}>
                      No hay evaluaciones disponibles
                    </td>
                  </tr>
                ) : (
                  paginatedEvaluaciones.map(e => (
                    <React.Fragment key={e.id}>
                      <tr>
                        <td>{e.id}</td>
                        <td>{e.date}</td>
                        <td>{e.time}</td>
                        <td>{e.device}</td>
                        <td>{e.location}</td>
                        <td>{e.ip}</td>
                      </tr>
                      <EvalRow>
                        <td colSpan={6}>
                          <EvalMetrics>
                            <EvalMetric>Limpieza: <Stars value={e.ratings.limpieza} /></EvalMetric>
                            <EvalMetric>Amabilidad: <Stars value={e.ratings.atencion} /></EvalMetric>
                            <EvalMetric>Rapidez: <Stars value={e.ratings.rapidez} /></EvalMetric>
                            <EvalMetric>Ayuda: <Stars value={e.ratings.ayuda} /></EvalMetric>
                            <EvalMetric>Solución: <Stars value={e.ratings.resolucion} /></EvalMetric>
                          </EvalMetrics>
                        </td>
                      </EvalRow>
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </Table>
            <TablePagination
              currentPage={evaluationsPage}
              totalItems={evaluaciones.length}
              pageSize={DETAIL_PAGE_SIZE}
              onPageChange={setEvaluationsPage}
            />
          </TableWrapper>
        </>
      )}

      {activeTab === "quizzes" && (
        <>
          {quizErrors.map((message, index) => (
            <ErrorMessage key={`${message}-${index}`}>⚠️ {message}</ErrorMessage>
          ))}

          {shouldShowQuizSkeleton && (
            <>
              <SkeletonLoader active={true} height="90px" margin="10px 0" borderRadius="12px" />
              <SkeletonLoader active={true} height="90px" margin="10px 0" borderRadius="12px" />
            </>
          )}

          {!shouldShowQuizSkeleton && redeemedTopics.length > 0 && (
            <>
              <SectionTitle>Resultados por tópico</SectionTitle>
              {redeemedTopics.map((topic) => {
                const topicId = getTopicId(topic);
                const topicProgress = quizProgressByTopic[topicId] || {};
                const topicHistory = quizHistoryByTopic[topicId] || {};
                const latestQuiz = topicHistory?.quizzes?.[0] || null;
                const totalQuestions = toNumber(
                  latestQuiz?.total_questions ?? topicProgress?.total_questions ?? topic?.total_questions,
                  0
                );
                const correctAnswers = toNumber(
                  topicProgress?.correct_answers ?? latestQuiz?.correct_answers,
                  0
                );
                const averageScore = correctAnswers * 2;
                const realProgress = calculateRealProgress({
                  correctAnswers,
                  totalQuestions,
                });

                return (
                  <TopicCard key={topicId}>
                    <TopicHeader>
                      <strong>{getTopicName(topic)}</strong>
                      <p>{topic?.description || "Sin descripción"}</p>
                    </TopicHeader>

                    <QuizSummaryGrid>
                      <QuizSummaryCard>
                        <strong>Estado</strong>
                        <span>{formatQuizStatus(topicProgress?.status, realProgress)}</span>
                      </QuizSummaryCard>
                      <QuizSummaryCard>
                        <strong>Progreso</strong>
                        <span>{toFixedOne(realProgress)}%</span>
                      </QuizSummaryCard>
                      <QuizSummaryCard>
                        <strong>Precisión</strong>
                        <span>{toFixedOne(topicProgress?.accuracy_percentage)}%</span>
                      </QuizSummaryCard>
                      <QuizSummaryCard>
                        <strong>Chapas</strong>
                        <span>{averageScore}</span>
                      </QuizSummaryCard>
                      <QuizSummaryCard>
                        <strong>Respondidas</strong>
                        <span>
                          {correctAnswers} / {totalQuestions}
                        </span>
                      </QuizSummaryCard>
                    </QuizSummaryGrid>

                    {topicHistory?.error && <ErrorMessage>⚠️ {topicHistory.error}</ErrorMessage>}
                  </TopicCard>
                );
              })}
            </>
          )}

          {showQuizEmptyState && (
            <EmptyMessage>No hay tópicos de trivia canjeados para este tenant.</EmptyMessage>
          )}
        </>
      )}
    </Wrapper>
  )
}
